import { GALLERY_CONFIG } from '../gallery-config.js';
import { formatPrice } from '../services/pricing/price-utils.js';

/**
 * Shared utility helpers for the gallery builder component.
 */
export const GalleryBuilderUtils = {
  decodeGalleryState(encoded) {
    if (!encoded || typeof encoded !== 'string') return null;

    const fromBase64Url = (value) => {
      const padded = value.replace(/-/g, '+').replace(/_/g, '/');
      const padLength = (4 - (padded.length % 4)) % 4;
      return padded.padEnd(padded.length + padLength, '=');
    };

    const decodeBinary = (base64) => {
      if (typeof window !== 'undefined' && typeof window.atob === 'function') {
        return window.atob(base64);
      }

      if (typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function') {
        return globalThis.atob(base64);
      }

      if (typeof Buffer !== 'undefined') {
        return Buffer.from(base64, 'base64').toString('binary');
      }

      throw new Error('No base64 decoder available');
    };

    try {
      const base64 = fromBase64Url(encoded);
      const binary = decodeBinary(base64);

      if (typeof TextDecoder !== 'undefined') {
        const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
        const decoded = new TextDecoder().decode(bytes);
        return JSON.parse(decoded);
      }

      const json = decodeURIComponent(escape(binary));
      return JSON.parse(json);
    } catch (error) {
      console.error('[GalleryBuilder] Failed to decode gallery state:', error);
      return null;
    }
  },

  normalizeDecodedState(rawState) {
    if (!rawState || typeof rawState !== 'object') return null;

    const normalizeLayoutCount = (value, fallback = 2) => {
      const count = Number(value);
      return count === 3 ? 3 : count === 2 ? 2 : fallback;
    };

    const normalizeVariantSelection = (selection = {}) => ({
      size: typeof selection.size === 'string' && selection.size.trim() ? selection.size : null,
      frame: typeof selection.frame === 'string' && selection.frame.trim() ? selection.frame : null,
      mount: typeof selection.mount === 'string' && selection.mount.trim() ? selection.mount : null,
      locked: !!selection.locked
    });

    const normalizeArtworkEntry = (entry) => {
      if (!entry || entry.id === undefined || entry.id === null) return null;

      const selectedVariant = entry.selected_variant && typeof entry.selected_variant === 'object'
        ? {
            id: entry.selected_variant.id ?? null,
            sku: entry.selected_variant.sku ?? null,
            title: entry.selected_variant.title ?? null,
            option1: entry.selected_variant.option1 ?? null,
            option2: entry.selected_variant.option2 ?? null,
            option3: entry.selected_variant.option3 ?? null
          }
        : null;

      return {
        id: entry.id,
        handle: entry.handle ?? null,
        selected_variant: selectedVariant
      };
    };

    const normalizeArtworkList = (artworks) => {
      if (!Array.isArray(artworks)) return [];
      return artworks
        .map(normalizeArtworkEntry)
        .filter(Boolean);
    };

    const orientation = rawState.orientation === 'vertical' ? 'vertical' : 'horizontal';
    const layout_count = normalizeLayoutCount(rawState.layout_count);
    const third_artwork_visible = !!rawState.third_artwork_visible;
    const variant_selection = normalizeVariantSelection(rawState.variant_selection);

    const orientation_states = ['horizontal', 'vertical'].reduce((accumulator, key) => {
      const source = rawState.orientation_states?.[key] || {};
      const layout = normalizeLayoutCount(source.layout, layout_count);

      accumulator[key] = {
        layout,
        selected_artworks: normalizeArtworkList(source.selected_artworks)
      };

      return accumulator;
    }, {});

    const active_selection = normalizeArtworkList(rawState.active_selection);

    return {
      version: rawState.version ?? 1,
      orientation,
      layout_count,
      third_artwork_visible,
      variant_selection,
      orientation_states,
      active_selection
    };
  },

  initializeSharedStateFromUrl() {
    if (typeof window === 'undefined') return;
    if (this._share_state_initialized) return;

    this._share_state_initialized = true;

    try {
      const params = new URLSearchParams(window.location.search);
      const encoded = params.get('gb_state');

      if (!encoded) {
        this._pending_share_state = null;
        return;
      }

      const decoded = this.decodeGalleryState(encoded);
      const normalized = this.normalizeDecodedState(decoded);

      if (!normalized) {
        this._pending_share_state = null;
        return;
      }

      this._pending_share_state = normalized;
      this._share_state_applied = false;

      const core_store = this.$store?.galleryCore;
      if (core_store) {
        this._share_state_fallback = {
          orientation: core_store.orientation,
          layout_count: core_store.layout_count,
          third_visible: core_store.third_artwork_visible
        };

        core_store.orientation = normalized.orientation;
        core_store.layout_count = normalized.layout_count;
        core_store.third_artwork_visible = normalized.layout_count === 3 && normalized.third_artwork_visible;

        if (normalized.orientation_states?.horizontal) {
          core_store.orientation_states.horizontal.layout = normalized.orientation_states.horizontal.layout;
        }

        if (normalized.orientation_states?.vertical) {
          core_store.orientation_states.vertical.layout = normalized.orientation_states.vertical.layout;
        }
      }
    } catch (error) {
      console.warn('[GalleryBuilder] Failed to initialize shared state from URL:', error);
      this._pending_share_state = null;
    }
  },

  hasPendingShareState() {
    return !!this._pending_share_state;
  },

  consumePendingShareState() {
    return this._pending_share_state || null;
  },

  clearPendingShareState() {
    this._pending_share_state = null;
    this._share_state_applied = false;
  },

  markShareStateApplied(success = true) {
    this._share_state_applied = !!success;

    if (success) {
      this._pending_share_state = null;
      this._share_state_fallback = null;
    } else if (this._share_state_fallback && this.$store?.galleryCore) {
      const core_store = this.$store.galleryCore;
      core_store.orientation = this._share_state_fallback.orientation ?? core_store.orientation;
      core_store.layout_count = this._share_state_fallback.layout_count ?? core_store.layout_count;
      core_store.third_artwork_visible = this._share_state_fallback.third_visible ?? core_store.third_artwork_visible;
      this._share_state_fallback = null;
    }
  },

  buildShareableState() {
    const core_store = this.$store?.galleryCore;

    if (!core_store) {
      console.warn('[GalleryBuilder] core store unavailable; cannot capture state');
      return null;
    }

    const serializeVariant = (variant) => {
      if (!variant) return null;

      return {
        id: variant.id ?? null,
        sku: variant.sku ?? null,
        title: variant.title ?? null,
        option1: variant.option1 ?? variant.options?.[0] ?? null,
        option2: variant.option2 ?? variant.options?.[1] ?? null,
        option3: variant.option3 ?? variant.options?.[2] ?? null
      };
    };

    const serializeArtworkList = (artworks) => {
      if (!Array.isArray(artworks)) return [];

      return artworks.map(artwork => {
        if (!artwork || !artwork.id) {
          return null;
        }

        return {
          id: artwork.id,
          handle: artwork.handle ?? null,
          selected_variant: serializeVariant(artwork.selected_variant ?? null)
        };
      });
    };

    const orientationStates = ['horizontal', 'vertical'].reduce((accumulator, orientationKey) => {
      const orientationState = core_store.orientation_states?.[orientationKey] || {};

      accumulator[orientationKey] = {
        layout: orientationState.layout ?? core_store.layout_count,
        selected_artworks: serializeArtworkList(orientationState.selected_artworks)
      };

      return accumulator;
    }, {});

    return {
      version: 1,
      orientation: core_store.orientation,
      layout_count: core_store.layout_count,
      third_artwork_visible: !!core_store.third_artwork_visible,
      variant_selection: {
        size: core_store.variant_selection?.size ?? null,
        frame: core_store.variant_selection?.frame ?? null,
        mount: core_store.variant_selection?.mount ?? null,
        locked: !!core_store.variant_selection?.locked
      },
      orientation_states: orientationStates,
      active_selection: serializeArtworkList(core_store.selected_artworks)
    };
  },

  encodeGalleryState(state) {
    if (!state) return null;

    const json = JSON.stringify(state);

    const toBase64Url = (base64) => base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    try {
      if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
        let binary = '';

        if (typeof TextEncoder !== 'undefined') {
          const bytes = new TextEncoder().encode(json);
          bytes.forEach(byte => {
            binary += String.fromCharCode(byte);
          });
        } else {
          binary = unescape(encodeURIComponent(json));
        }

        return toBase64Url(window.btoa(binary));
      }

      if (typeof globalThis !== 'undefined' && typeof globalThis.btoa === 'function') {
        let binary = '';

        if (typeof TextEncoder !== 'undefined') {
          const bytes = new TextEncoder().encode(json);
          bytes.forEach(byte => {
            binary += String.fromCharCode(byte);
          });
        } else {
          binary = unescape(encodeURIComponent(json));
        }

        return toBase64Url(globalThis.btoa(binary));
      }

      if (typeof Buffer !== 'undefined') {
        return toBase64Url(Buffer.from(json, 'utf-8').toString('base64'));
      }
    } catch (error) {
      console.error('[GalleryBuilder] Failed to encode gallery state:', error);
      return null;
    }

    console.warn('[GalleryBuilder] No base64 encoder available; skipping shareable URL generation');
    return null;
  },

  buildShareableUrl() {
    const state = this.buildShareableState();
    if (!state) return null;

    const encoded = this.encodeGalleryState(state);
    if (!encoded) return null;

    const { protocol, host, pathname } = window.location;
    const baseUrl = `${protocol}//${host}${pathname}`;

    return `${baseUrl}?gb_state=${encoded}`;
  },

  /**
   * Display Klaviyo form with shareable URL
   * @param {string} formId - The ID of the form element to display
   * @param {string} shareableUrl - The shareable URL to inject into the form
   * @param {Function} onClose - Optional callback to execute when form is closed
   * @returns {Promise<boolean>} - Returns true if form was successfully displayed
   */
  async _handleKlaviyoForm(formId, shareableUrl, onClose = null) {
    if (!shareableUrl) {
      console.warn('[GalleryBuilder] Unable to build shareable URL');
      return false;
    }

    const form = document.getElementById(formId);
    if (!form) {
      console.warn(`[GalleryBuilder] Form with ID ${formId} not found`);
      return false;
    }

    const formElement = form.querySelector('form');
    if (!formElement) {
      console.warn(`[GalleryBuilder] Form element not found inside ${formId}`);
      return false;
    }

    form.style.display = 'block';

    const closeButton = document.createElement('button');
    closeButton.className = 'gallery-builder-klaviyo-close';
    closeButton.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5 5L15 15" stroke="black" stroke-linecap="round"/>
<path d="M15 5L5 15" stroke="black" stroke-linecap="round"/>
</svg>`;
    closeButton.setAttribute('type', 'button');
    closeButton.setAttribute('aria-label', 'Close form');

    if (!formElement.querySelector('.gallery-builder-klaviyo-close')) {
      formElement.appendChild(closeButton);
    }

    const closeForm = () => {
      form.style.display = 'none';
      const wrapper = form.querySelector(':scope > div');
      if (wrapper) {
        wrapper.removeEventListener('click', handleWrapperClick);
      }
      closeButton.removeEventListener('click', closeForm);

      if (onClose && typeof onClose === 'function') {
        onClose();
      }
    };

    closeButton.addEventListener('click', closeForm);

    const handleWrapperClick = (e) => {
      const wrapper = form.querySelector(':scope > div');
      if (e.target === wrapper) {
        closeForm();
      }
    };
    form.addEventListener('click', handleWrapperClick);

    const pollForSubmitButton = setInterval(() => {
      const submitButton = form.querySelector('[data-testid="form-row"]:last-child button');
      if (submitButton && !submitButton.dataset.closeHandlerAttached) {
        submitButton.dataset.closeHandlerAttached = 'true';
        submitButton.addEventListener('click', () => {
          setTimeout(closeForm, 500);
        });
        clearInterval(pollForSubmitButton);
      }
    }, 100);

    setTimeout(() => {
      clearInterval(pollForSubmitButton);
    }, 5000);

    const pollForInput = setInterval(() => {
      const urlInput = form.querySelector('input[type="text"]');
      if (urlInput) {
        urlInput.value = shareableUrl;

        urlInput.dispatchEvent(new Event('input', { bubbles: true }));
        urlInput.dispatchEvent(new Event('change', { bubbles: true }));

        clearInterval(pollForInput);
      }
    }, 100);

    setTimeout(() => {
      clearInterval(pollForInput);
      console.warn(`[GalleryBuilder] Klaviyo form input field not found after 5 seconds in ${formId}`);
    }, 5000);

    return true;
  },

  async saveGalleryConfiguration() {
    const shareableUrl = this.buildShareableUrl();
    return await this._handleKlaviyoForm('galleryBuilderKlaviyoSaveForm', shareableUrl);
  },

  async handleExitWithForm() {
    const shareableUrl = this.buildShareableUrl();

    return await this._handleKlaviyoForm(
      'galleryBuilderKlaviyoExitForm',
      shareableUrl,
      () => {
        if (this.handleExit && typeof this.handleExit === 'function') {
          this.handleExit();
        }
      }
    );
  },

  handleError(method_name, error) {
    const error_message = `${method_name}: ${error.message}`;
    this.$store.galleryUI.setError(error_message);
    this.$store.galleryGlobal.setError(error.message);

    console.error(`Gallery error in ${method_name}:`, error);

    if (
      this.$store.galleryUI.retry_count < GALLERY_CONFIG.PERFORMANCE.RETRY_LIMIT &&
      method_name === 'loadCollectionProducts'
    ) {
      this.$store.galleryUI.incrementRetry();
      setTimeout(() => this.loadCollectionProducts(), GALLERY_CONFIG.PERFORMANCE.RETRY_DELAY);
    }
  },

  get show_member_banner() {
    const hasProducts = (this.$store?.galleryProducts?.filtered_artworks?.length || 0) > 0;
    const isDismissed = sessionStorage.getItem('gallery_member_banner_dismissed') === 'true';
    const hasSizeSelected = this.$store?.galleryCore?.variant_selection?.size !== null &&
                            this.$store?.galleryCore?.variant_selection?.size !== undefined;

    return hasProducts && !isDismissed && hasSizeSelected;
  },

  get show_modal_member_banner() {
    const hasProducts = (this.$store?.galleryProducts?.filtered_artworks?.length || 0) > 0;
    const hasSizeSelected = this.$store?.galleryCore?.variant_selection?.size !== null &&
                            this.$store?.galleryCore?.variant_selection?.size !== undefined;

    return hasProducts && hasSizeSelected;
  },

  get member_price_display() {
    const variant_selection = this.$store?.galleryCore?.variant_selection;
    const hasVariantSelection = variant_selection?.size !== null && variant_selection?.size !== undefined;

    if (hasVariantSelection) {
      const price = this.calculate_member_total();
      return price && price !== '$0' ? price : '';
    }

    const hasProducts = (this.$store?.galleryProducts?.filtered_artworks?.length || 0) > 0;
    if (hasProducts && this.show_member_banner) {
      const fromPrice = this.calculate_from_member_price();
      return fromPrice && fromPrice !== '$0' ? fromPrice : '';
    }

    return '';
  },

  calculate_member_total() {
    const selected_artworks = this.$store?.galleryCore?.selected_artworks?.filter(Boolean) || [];
    const variant_selection = this.$store?.galleryCore?.variant_selection || {};

    if (!selected_artworks.length || !variant_selection?.size) {
      return '';
    }

    const variant_manager = this.$store?.galleryCore?.variant_manager;
    if (!variant_manager) {
      return '';
    }

    try {
      const total_member_price = variant_manager.calculateMemberPrice(
        selected_artworks,
        variant_selection.size,
        variant_selection.frame,
        variant_selection.mount
      );

      return total_member_price > 0 ? formatPrice(total_member_price) : '';
    } catch (error) {
      console.warn('[GalleryBuilder] Error calculating member total:', error);
      return '';
    }
  },

  /**
   * Calculate minimum "from" member price for gallery layout
   * Follows same pattern as min_price_display: cheapest variant × gallery slots
   */
  calculate_from_member_price() {
    const selected_artworks = this.$store?.galleryCore?.selected_artworks || [];
    const filtered_artworks = this.$store?.galleryProducts?.filtered_artworks || [];

    if (!selected_artworks.length || !filtered_artworks.length) {
      return '';
    }

    try {
      let min_member_price = Infinity;

      filtered_artworks.forEach(artwork => {
        if (artwork?.variants?.length) {
          artwork.variants.forEach(variant => {
            if (variant.available) {
              const member_price = variant.compare_at_price && parseFloat(variant.compare_at_price) > 0
                ? parseFloat(variant.compare_at_price) * 100
                : parseFloat(variant.price || 0) * 100;

              if (member_price > 0 && member_price < min_member_price) {
                min_member_price = member_price;
              }
            }
          });
        }
      });

      if (min_member_price === Infinity || min_member_price <= 0) return '';

      const total_from_price = min_member_price * selected_artworks.length;

      return total_from_price > 0 ? formatPrice(total_from_price) : '';
    } catch (error) {
      console.warn('[GalleryBuilder] Error calculating from member price:', error);
      return '';
    }
  },

  close_member_banner() {
    sessionStorage.setItem('gallery_member_banner_dismissed', 'true');

    const footerBanner = document.querySelector('.gallery-member-banner--footer');
    if (footerBanner) {
      footerBanner.remove();
    }
  },

  open_membership_modal() {
    console.debug('Gallery Builder: attempting to open membership modal.');
    const membershipStore =
      this.$store?.membership ||
      (typeof Alpine !== 'undefined' && typeof Alpine.store === 'function'
        ? Alpine.store('membership')
        : undefined);

    if (!membershipStore) {
      console.warn('Gallery Builder: membership store is unavailable.');
      return;
    }

    console.debug('Gallery Builder: membership store located.', {
      hasOpenMemberShipModal: typeof membershipStore.openMemberShipModal === 'function',
      hasSwitchForm: typeof membershipStore.switchForm === 'function'
    });

    if (typeof membershipStore.openMemberShipModal === 'function') {
      console.debug('Gallery Builder: invoking membershipStore.openMemberShipModal().');
      membershipStore.openMemberShipModal();
    }

    if (typeof membershipStore.switchForm === 'function') {
      console.debug("Gallery Builder: invoking membershipStore.switchForm('registration').");
      membershipStore.switchForm('registration');
    }
  },

  setCookie(name, value, days) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  },

  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
};
